import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS types
import { RootState } from '@redux/store';
import { IFormat, Area } from '@redux/common';

// component
import GDSPlan from '@component/GDSTHSR/gdsPlan'; // GDS各項清單明細
import DetailInfo from '@component/GDSTHSR/subComponent/DetailInfo'; // GDS各項清單明細
import Tags from '@component/GDSTHSR/subComponent/Tags';
import Informations from '@component/GDSTHSR/subComponent/Informations';
import Dialog from '@component/Common/Dialog';

// shared component
import LoadingIcon from '@svg/LoadingIcon'; // svg component
import Reload from '@svg/Reload';
import Loading from '@Common/loading';
import Modal from '@Common/Modal';
import SlideFooter from '@Common/SlideFooter';

// styles
import '../CommonStyles/TPlans.scss';

// common function
import { getTHSRTripInfo, THSRClearStorage, checkLigBoxOrDialogDisplay, formatThsrQtyReqBody } from '@libs/core';
import { fetchGDSDetailData, fetchLightBoxData } from '@libs/apiCall';
import { isMobile } from '@libs/globalVariables';
import onSubmit from './Common/onSubmit';

// plugin
import dayjs from 'dayjs';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setTGRestore,
    setCalendar,
    setTGMInfoShow,
    setTGNowListData,
    setTGNowList,
    setSession,
    setSessionPicked,
    setTGDSDetailFetchFail,
    setGDetail,
    setGTempCalendarData,
    setIsTGDSDetailLoading,
    setIsTGDSSessionLoading,
    setTGDSSessionFetchFail,
    setIsSubmitSuccess,
} from '@redux/GDST/actions';
import { setTCurrentStep, setTHSROptsMOpen } from '@redux/THSR/actions';
import { setGDetailStore, setDetailFetchFailMsg } from '@redux/Common/actions';

import { getCheckThsrQty } from '@libs/apiCall';

interface GListPanelProps {
    isCrossell?: boolean;
}

const TGdsListPanel: React.FC<GListPanelProps> = (props) => {
    const { isCrossell } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        GDetail,
        TGNowList,
        TGMInfoShow,
        isTGDSSessionLoading,
        isTGDSDetailLoading,
        TGDSDetailFetchFail,
        isSubmitSuccess,
    } = useSelector((state: RootState) => state.GDST);
    const {
        isDisney,
        GDetailStore,
        ETID,
        GListData,
        focusArea,
        DetailFetchFailMsg,
        VerificationStatus,
    } = useSelector((state: RootState) => state.Common);
    const { THSROpenedPanel } = useSelector((state: RootState) => state.THSR);
    const store_THSR = useSelector((state: RootState) => state.THSR);
    const [renderLists, setRenderLists] = useState<IFormat[]>(null);

    const [dialogSwitch, setDialogSwitch] = useState<{ [key: string]: boolean }>({}); //Dialog-State object
    const [fetchLightBoxResult, setFetchLightBoxResult] = useState({}); //Fetch lightBox save result state

    // _start：迪士尼光箱開發區
    // TODO:迪士尼對話框光箱開發暫時設置的"假資料"，待後續等API完成，再行移除
    const lightBoxMockData: {
        [key: string]: {
            title?: string;
            content?: string;
        };
    }[] = [
        fetchLightBoxResult, //Fetch json
    ];
    const disneyLightBoxData = lightBoxMockData[0].disney;
    console.log('TDisneyLightBoxData', disneyLightBoxData);
    console.log('TFetchLightBoxResult!', fetchLightBoxResult);
    console.log('TDialogSwitch', dialogSwitch);
    // _END：迪士尼光箱開發區

    // Fetch LightBox object data & init dialog state
    useEffect(() => {
        const getLightBoxData = async () => {
            const getData = await fetchLightBoxData();
            const result = await getData.json();

            setFetchLightBoxResult(result);
        };

        getLightBoxData();

        /** 初始化 dialog state
         *  無法一開始就取得資料的光箱才在下面新增，其他等打完API後再在useEffect做資料確認&新增
         */
        setDialogSwitch({
            // 預設基本會有的光箱類型
            verify: false, // 驗價光箱
        });
    }, []);

    // Dialog component switch object settings
    useEffect(() => {
        /* 一開始就可以打API拿到資料的光箱 */
        //迪士尼對話框光箱
        if (isDisney && !!disneyLightBoxData && Object.keys(disneyLightBoxData).length) {
            setDialogSwitch((prev) => ({ ...prev, disney: false }));
        }
    }, [isDisney, disneyLightBoxData, fetchLightBoxResult]);

    useEffect(() => {
        if (!GListData || !store_THSR.THSRInitData) return;
        // 篩選出單程或來回的ProductItemListFormat
        const currentListFormat = getTHSRTripInfo(
            store_THSR.THSRInitData.ThsrTripInfos,
            THSROpenedPanel
        ).ProductItemListFormat;

        setRenderLists(currentListFormat);

        if (!isMobile) {
            // PC版初次載入頁面預選第一筆
            if (currentListFormat[0].EarliestBuyDay) {
                dispatch(
                    setCalendar(dayjs(currentListFormat[0].EarliestBuyDay).format('YYYY-MM-DD'))
                );
            } else {
                dispatch(setCalendar(null));
            }

            dispatch(setTGNowListData(currentListFormat[0]));
            dispatch(setTGNowList(currentListFormat[0].FormatID));

            const fetchDetailProps = {
                ETID: ETID,
                EarliestBuyDay: currentListFormat[0].EarliestBuyDay,
                ExpectedUseDay: currentListFormat[0].ExpectedUseDay,
                FormatID: currentListFormat[0].FormatID,
                isTHSR: true,
                context: {
                    GDetailStore,
                    setGDetailStore,
                    setGDSDetailFetchFail: setTGDSDetailFetchFail,
                    setDetailFetchFailMsg,
                    setGDetail,
                    setGTempCalendarData,
                    setIsGDSDetailLoading: setIsTGDSDetailLoading,
                    setCalendar: setCalendar,
                    ThsrRoundType: THSROpenedPanel,
                    setGSessionLoading: setIsTGDSSessionLoading,
                    setGSessionFetchFail: setTGDSSessionFetchFail,
                    setSession,
                    setSessionPicked,
                },
                dispatch,
            };

            fetchGDSDetailData(fetchDetailProps);
        }
    }, [GListData]);

    useEffect(() => {
        if (TGNowList && focusArea === Area.Origin) {
            dispatch(setTGRestore());
        }
    }, [focusArea]);

    const clickMSubmitBtn = () => {
        if (isSubmitSuccess) return;
        // 送出前清空高鐵相關sessionStorage
        THSRClearStorage();

        let reqBody = formatThsrQtyReqBody(
            store_THSR.THSRDetailData.Rounds,
            ETID,
            store_THSR?.THSRDepartTrain,
            store_THSR?.THSRReturnTrain,
            store_THSR?.THSRInfo
        );

        //需驗證高鐵是否還有庫存
        getCheckThsrQty({
            context: store_THSR,
            req: reqBody,
        }).then(async (res) => {
            if (res.every((child) => child?.HasQty)) {
                //有庫存
                const isSuccess = await onSubmit('checkData');
                if (typeof isSuccess === 'undefined') return;
                if (isSuccess.selectChecked) {
                    // 確認是否為迪士尼產品
                    if (checkLigBoxOrDialogDisplay(dialogSwitch, setDialogSwitch, 'disney')) {
                        setDialogSwitch((prev) => {
                            prev.disney = true;
                            return { ...prev };
                        });
                    } else {
                        dispatch(setIsSubmitSuccess(true));
                        onSubmit('redirect');
                    }
                } else if (
                    !isSuccess.selectChecked &&
                    'IsSuccess' in isSuccess.apiChecked &&
                    !isSuccess.apiChecked.IsSuccess 
                ) {
                    // 驗價錯誤則顯示提示重整頁面光箱
                    setDialogSwitch((prev) => {
                        prev.verify = true;
                        return { ...prev };
                    });
                }
            } else {
                //無庫存or其他錯誤
                window.alert(t('THSR.index.noSeatErr', { symbol: '！' }));
                window.location.reload();
            }
        });
    };

    return (
        <div className="THSRBlock Tgdsplan">
            <div className="ActivityPanel">
                <div className="mainBLock">
                    <div className="optionsWrap">
                        <div className="optionTitleWrap">
                            <div className="subTitle">{t('THSR.index.choose_plan')}</div>
                        </div>
                        <div className="listWrap">
                            {renderLists && renderLists.length !== 0 ? (
                                renderLists.map((list: IFormat, index: number) => (
                                    <GDSPlan
                                        data={list}
                                        key={list.FormatID}
                                        Currency={GListData.Currency}
                                        SaleCurrency={GListData.SaleCurrency}
                                        listIndex={index}
                                        isCrossell={isCrossell}
                                        AdvanceTime={GListData.AdvanceTime}
                                    />
                                ))
                            ) : (
                                <div>{t('common.noListFormat')}</div>
                            )}
                        </div>
                    </div>
                    {isTGDSDetailLoading ? (
                        <Loading icon={<LoadingIcon />} text={t('common.loading_project')} />
                    ) : TGDSDetailFetchFail ? (
                        <div>
                            {DetailFetchFailMsg
                                ? t(`common.${DetailFetchFailMsg}`)
                                : t('common.defaultErrMsg')}
                        </div>
                    ) : (
                        <DetailInfo dialogSwitch={dialogSwitch} setDialogSwitch={setDialogSwitch} isCrossell={isCrossell}/>
                    )}
                </div>
                {isMobile ? (
                    <Modal
                        isOpen={TGMInfoShow}
                        title={(() => {
                            const currentFormat = GListData?.Format?.filter(
                                (item) => item.FormatID === TGNowList
                            );
                            return currentFormat ? currentFormat[0]?.FormatName : '';
                        })()}
                        onClose={(isShow) => dispatch(setTGMInfoShow(isShow))}
                    >
                        {isTGDSDetailLoading ? (
                            <Loading icon={<LoadingIcon />} text={t('common.loading_project')} />
                        ) : TGDSDetailFetchFail ? (
                            <div>
                                {DetailFetchFailMsg
                                    ? t(`common.${DetailFetchFailMsg}`)
                                    : t('common.defaultErrMsg')}
                            </div>
                        ) : (
                            GDetail && (
                                <>
                                    <Tags />
                                    <Informations formatId={TGNowList}/>
                                </>
                            )
                        )}
                    </Modal>
                ) : (
                    !isTGDSDetailLoading &&
                    GDetail && (
                        <>
                            <Tags />
                            <Informations formatId={TGNowList}/>
                        </>
                    )
                )}
                {isMobile &&
                    !isTGDSDetailLoading &&
                    !isTGDSSessionLoading &&
                    !TGDSDetailFetchFail && (
                        <SlideFooter
                            hide={!TGNowList || TGMInfoShow}
                            onClickLeftBtn={() => {
                                dispatch(setTHSROptsMOpen(false));
                                dispatch(setTCurrentStep(0));
                                dispatch(setTGRestore());
                            }}
                            onClickRightBtn={async () => {
                                clickMSubmitBtn();
                            }}
                            leftBtnText={t('THSR.index.cancelAndResetTHSR')}
                            rightBtnClass={[
                                'planFoot',
                                {
                                    disabled: isSubmitSuccess,
                                },
                            ]}
                            rightBtnJSX={
                                <div className={cx('confirm')}>
                                    <div>{t('THSR.index.bookNow')}</div>
                                    {isSubmitSuccess && (
                                        <div className="reload">
                                            <Reload color="#ffffff" />
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    )}
            </div>
            {/* 迪士尼對話框光箱 */}
            <Dialog
                type="default"
                title={disneyLightBoxData?.title ?? ''}
                body={disneyLightBoxData?.content ?? ''}
                open={dialogSwitch.disney}
                onClose={() => {
                    console.log('Close Disney LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                }}
                // onCancel={() => {
                //     console.log('cancel!')
                //     console.log(dialogSwitch)
                //     setDialogSwitch(prev => {
                //         prev.disney = false;
                //         return {...prev};
                //     })
                // }}
                onOK={() => {
                    console.log('Disney CheckOK!');
                    dispatch(setIsSubmitSuccess(true));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                    onSubmit('redirect');
                }}
                OKText={t('THSR.index.IHaveRead')}
                showCloseButton={true}
                className={cx('disney')}
            />
            {/* 驗價提示光箱 */}
            <Dialog
                type="default"
                title={VerificationStatus?.Title ?? ''}
                body={VerificationStatus?.ErrMessage ?? 'fetch API error, please reset page'}
                open={dialogSwitch.verify}
                onClose={() => {
                    console.log('Close Verify LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                }}
                onOK={() => {
                    // 按下已閱讀後重新整理頁面
                    console.log('validate error, reset detailPage...');
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                    window.location.reload();
                }}
                OKText={t('THSR.index.IHaveRead')}
                showCloseButton={true}
                className={cx('orderValidity')}
            />
        </div>
    );
};

export default TGdsListPanel;
